/**
 * Slug helpers for SEO-friendly URLs.
 *
 * The directory uses three URL shapes:
 *   /category/<category-slug>-in-<place-slug>   e.g. /category/restaurants-in-mysore
 *   /business/<business-slug>-<place-slug>      e.g. /business/mylari-mane-restaurant-mysore
 *   /location/<place-slug>                      e.g. /location/mysore
 *
 * The plain single-slug forms (/category/restaurants, /business/mylari-mane-restaurant)
 * keep working too — the location segment is optional. Canonical links always
 * point at the compound form so search engines index one URL per page.
 */

/** "-in-" joins a category slug to a place slug. */
export const PLACE_CONNECTOR = "-in-";

/** Turns arbitrary text into a lowercase, hyphenated, URL-safe slug. */
export function slugify(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
}

/**
 * Returns a slug that does not collide with any already-taken slug by
 * appending `-2`, `-3`, … until it is unique. This is the safe duplicate
 * handler used when two businesses (or categories) resolve to the same base
 * slug — the first keeps the clean slug, the rest get a numeric suffix.
 */
export function uniqueSlug(base: string, taken: Iterable<string>): string {
  const root = slugify(base) || "listing";
  const used = new Set<string>();
  for (const item of taken) {
    const normalized = slugify(item);
    if (normalized) used.add(normalized);
  }
  if (!used.has(root)) return root;

  let counter = 2;
  let candidate = `${root}-${counter}`;
  while (used.has(candidate)) {
    counter += 1;
    candidate = `${root}-${counter}`;
  }
  return candidate;
}

/** Builds `/category/<category>-in-<place>` (place omitted when absent). */
export function categoryPlaceSlug(categorySlug: string, placeSlug?: string | null): string {
  const category = slugify(categorySlug);
  const place = placeSlug ? slugify(placeSlug) : "";
  return place ? `${category}${PLACE_CONNECTOR}${place}` : category;
}

/** Splits `/category/<category>-in-<place>` back into its two slugs. */
export function parseCategoryPlaceSlug(slug: string): {
  categorySlug: string;
  placeSlug: string | null;
} {
  const index = slug.lastIndexOf(PLACE_CONNECTOR);
  if (index <= 0) return { categorySlug: slug, placeSlug: null };
  const categorySlug = slug.slice(0, index);
  const placeSlug = slug.slice(index + PLACE_CONNECTOR.length);
  if (!categorySlug || !placeSlug) return { categorySlug: slug, placeSlug: null };
  return { categorySlug, placeSlug };
}

/** Builds `/business/<business>-<place>` (place omitted when absent). */
export function businessPlaceSlug(businessSlug: string, placeSlug?: string | null): string {
  const business = slugify(businessSlug);
  const place = placeSlug ? slugify(placeSlug) : "";
  return place ? `${business}-${place}` : business;
}

/**
 * Recovers the base business slug from a compound URL by stripping a trailing
 * `-<known place slug>`. Only known place slugs are stripped, so a business
 * whose own name ends in a real word is never truncated by accident.
 */
export function parseBusinessPlaceSlug(
  slug: string,
  placeSlugs: Iterable<string>,
): { businessSlug: string; placeSlug: string | null } {
  const normalized = slugify(slug);
  for (const place of placeSlugs) {
    const placeSlug = slugify(place);
    const suffix = `-${placeSlug}`;
    if (placeSlug && normalized.length > suffix.length && normalized.endsWith(suffix)) {
      return { businessSlug: normalized.slice(0, -suffix.length), placeSlug };
    }
  }
  return { businessSlug: normalized, placeSlug: null };
}

/** Resolves the best place slug for a listing: explicit slug first, else the name. */
export function placeSlugFor(item: { locationSlug?: string; locationName?: string }): string {
  return item.locationSlug ? slugify(item.locationSlug) : slugify(item.locationName);
}

/** Route path for a business listing, with the place segment appended. */
export function businessPath(item: {
  slug: string;
  locationSlug?: string;
  locationName?: string;
}): string {
  return `/business/${businessPlaceSlug(item.slug, placeSlugFor(item))}`;
}

/** Route path for a category, optionally scoped to a place. */
export function categoryPath(categorySlug: string, placeSlug?: string | null): string {
  return `/category/${categoryPlaceSlug(categorySlug, placeSlug)}`;
}

/** Route path for a location page. */
export function locationPath(placeSlug: string): string {
  return `/location/${slugify(placeSlug)}`;
}